import { INTEGRATION_PROVIDERS } from '@vy/types';
import type { FC } from 'react';
import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { isNotEmptyString } from '@vy/utils';
import { useInvoiceProductsIntegrations } from '../../../../../../../../../../../hooks/requests/use-invoice-products-integrations';
import { usePaddleProducts } from '../../../../../../../../../../../hooks/requests/integrations';
import { EditPaddleProductDialog } from './EditPaddleProductDialog';

type PaddleInvoiceProductsAccordionContentProps = {
  invoiceId: string;
  disabled: boolean;
};

export const PaddleInvoiceProductsAccordionContent: FC<PaddleInvoiceProductsAccordionContentProps> = ({
  invoiceId,
  disabled,
}) => {
  const { data: invoiceProductsIntegrations } = useInvoiceProductsIntegrations(
    invoiceId,
    INTEGRATION_PROVIDERS.paddle,
    {
      suspense: true,
    },
  );

  const { data: paddleProducts } = usePaddleProducts();

  const [openDialogId, setOpenDialogId] = useState<string | null>(null);
  const products = invoiceProductsIntegrations?.products ?? [];

  const handleOpen = (productId: string) => {
    setOpenDialogId(productId);
  };

  const handleClose = () => {
    setOpenDialogId(null);
  };

  return (
    <Stack
      direction="column"
      sx={{
        gap: '16px',
      }}
    >
      {products.map((product) => {
        const hasPaddleMapping = isNotEmptyString(product.externalId);

        return (
          <Box
            key={product.id}
            sx={{
              border: '1px solid #E8EBF2',
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#F5F6FA',
                padding: '0 16px',
                height: '32px',
              }}
            >
              <Typography
                sx={{
                  lineHeight: '20px',
                  fontSize: '14px',
                  color: 'text.secondary',
                }}
              >
                {product.name}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<VyIcon icon={hasPaddleMapping ? 'Pen' : 'PlusMedium'} />}
                disabled={disabled}
                onClick={() => handleOpen(product.id)}
                sx={{ fontSize: '12px', lineHeight: '16px' }}
              >
                {hasPaddleMapping ? 'Edit' : 'Add'}
              </Button>
            </Stack>
            <Stack
              direction="column"
              sx={{
                gap: '8px',
                padding: '15px 16px',
              }}
            >
              {hasPaddleMapping ? (
                isNotEmptyString(product.externalName) && (
                  <Typography
                    sx={{
                      lineHeight: '20px',
                      fontSize: '14px',
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Paddle name:
                    </Box>{' '}
                    {product.externalName}
                  </Typography>
                )
              ) : (
                <Typography
                  sx={{
                    lineHeight: '20px',
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                >
                  No custom fields here. Click Add to add
                </Typography>
              )}
            </Stack>
            <EditPaddleProductDialog
              open={openDialogId === product.id}
              onClose={handleClose}
              productId={product.id}
              externalProductId={product.externalId}
              paddleProducts={paddleProducts?.products ?? []}
            />
          </Box>
        );
      })}
    </Stack>
  );
};
